/**
 * useSubscribers Hook
 *
 * Feature-specific hook for the Subscribers page.
 *
 * Architecture: Page → Hook → UseCase → Service
 *
 * Mirrors Flutter's SubscribersScreenState
 * (lib/presentation/screens/subscriptions/subscribers_screen.dart).
 *
 * Responsibilities:
 * - Fetch one page of subscribers at a time via
 *   `fetchExclusiveContentSubscribersPageUseCase`, using the Firestore
 *   opaque `PageCursor` returned by the previous page.
 * - Surface loading / loading-more / hasMore / error to the UI.
 *
 * Unlike the unlocked-videos page, subscribers are NOT mirrored locally —
 * the list comes purely from the server, so there is no SecureStorage
 * listener or live-update channel to wire up here.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { PageCursor } from '@/data/types/json';
import type { ExclusiveContentSubscriptionEntity } from '@/domain/entities/exclusiveContentSubscription/exclusiveContentSubscriptionEntity';
import type { RootState } from '@/presentation/store';
import { fetchExclusiveContentSubscribersPageUseCase } from '@/domain/usecases/exclusiveContentSubscription/fetchExclusiveContentSubscribersPageUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { useLocale } from '@/presentation/contexts/LocaleContext';

export interface UseSubscribersResult {
  subscribers: ExclusiveContentSubscriptionEntity[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  init: () => Promise<void>;
  loadMore: () => Promise<void>;
}

export const useSubscribers = (): UseSubscribersResult => {
  const { l10n } = useLocale();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Mirror `currentUser` into a ref so `init` / `loadMore` aren't re-created
  // every time the auth listener mirrors SecureStorage into Redux (which
  // produces a fresh `UserEntity` reference even when nothing relevant
  // changed — see .claude/context/state-slices.md). The use case needs the
  // full object, so ref-mirroring is cleaner than pulling out scalars.
  const currentUserRef = useRef(currentUser);
  useEffect(() => {
    currentUserRef.current = currentUser;
  });

  const [subscribers, setSubscribers] = useState<ExclusiveContentSubscriptionEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cursorRef = useRef<PageCursor | undefined>(undefined);
  const isLoadingMoreRef = useRef(false);

  const init = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    cursorRef.current = undefined;

    try {
      const result = await fetchExclusiveContentSubscribersPageUseCase.execute({
        currentUser: currentUserRef.current,
      });

      cursorRef.current = result.cursor;
      setSubscribers(result.subscribers);
      setHasMore(result.hasMore);
    } catch (err) {
      appLogger.error('Error fetching subscribers', 'useSubscribers.init', { error: err as Error });
      setError(l10n.subscribers.errorLoading);
      setSubscribers([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [l10n]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (!currentUserRef.current) {
      return;
    }
    if (isLoadingMoreRef.current || !hasMore) {
      return;
    }

    setIsLoadingMore(true);
    isLoadingMoreRef.current = true;
    setError(null);

    try {
      const result = await fetchExclusiveContentSubscribersPageUseCase.execute({
        currentUser: currentUserRef.current,
        cursor: cursorRef.current,
      });

      cursorRef.current = result.cursor;
      setSubscribers((prev) => [...prev, ...result.subscribers]);
      setHasMore(result.hasMore);
    } catch (err) {
      appLogger.error('Error loading more subscribers', 'useSubscribers.loadMore', { error: err as Error });
      setError(l10n.subscribers.loadMoreFailed);
    } finally {
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
    }
  }, [hasMore, l10n]);

  return {
    subscribers,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    init,
    loadMore,
  };
};
